import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaCalendarAlt, FaUser, FaCrown } from 'react-icons/fa';
import api from '../services/apiService';
import { authService } from '../services/authService';
import ProFeatureModal from './ProFeatureModal';
import '../scss/matchReports.scss';

const MatchReports = ({ showDummyData = false }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProModal, setShowProModal] = useState(false);
  const [requiresPro, setRequiresPro] = useState(false);
  const navigate = useNavigate();
  const isPro = authService.isPro();
  const isManager = authService.isManager();
  const isAdmin = authService.isAdmin();



  useEffect(() => {
    const fetchMatchReports = async () => {
      try {
        setLoading(true);
        const teamId = sessionStorage.getItem('selected_team_id');
        
        if (!teamId) {
          setError('No team selected');
          setLoading(false);
          return;
        }

        // Build the API URL - simplified to show all team match reports
        const apiUrl = `${process.env.REACT_APP_API_URL}/get_team_match_reports.php?team_id=${teamId}`;
        
        const response = await api.get(apiUrl);
        
        if (response.data.success) {
          setReports(response.data.reports || []);
          if (response.data.limited_access) {
            setRequiresPro(true);
          }
        } else {
          if (response.data.requires_pro) {
            setRequiresPro(true);
            setError('Pro subscription required to view match reports');
          } else {
            setError(response.data.message || 'Failed to fetch match reports');
          }
        }
      } catch (err) {
        console.error('Error fetching match reports:', err);
        setError('Failed to load match reports');
      } finally {
        setLoading(false);
      }
    };

    fetchMatchReports();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleReportClick = (matchId, reportIndex) => {
    // First report (index 0) is always accessible for non-pro users
    if (!requiresPro || reportIndex === 0) {
      navigate(`/match/${matchId}/report`);
    } else {
      setShowProModal(true);
    }
  };

  if (loading) {
    return (
      <div className="matches-container dashboard">
        <div className="matches-container-header container">
          <h2 className="section-title">Match Reports</h2>
        </div>
        <div className="container">
          <p style={{color: '#585858', marginTop: '0'}}>Loading match reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="matches-container dashboard">
        <div className="matches-container-header container">
          <h2 className="section-title">Match Reports</h2>
        </div>
        <div className="container">
          {requiresPro ? (
            <div className="pro-feature-notice" onClick={() => setShowProModal(true)}>
              <FaCrown className="crown-icon" />
              <span>Upgrade to Pro to View Match Reports</span>
            </div>
          ) : (
            <p style={{color: '#585858', marginTop: '0'}}>{error}</p>
          )}
        </div>
        <ProFeatureModal
          isOpen={showProModal}
          onClose={() => setShowProModal(false)}
          featureName="Match Reports"
        />
      </div>
    );
  }

  if (reports.length === 0) {
    // Only show dummy data if showDummyData is true
    if (showDummyData) {
      // Add dummy match report data for display purposes
      const dummyReports = [
        {
          report_id: 'dummy-1',
          match_id: '1',
          title: 'Thrilling 3-2 Victory Against FC United',
          excerpt: 'An exciting match that showcased excellent teamwork and determination. The team showed great character to come back from behind twice and secure a well-deserved victory.',
          match_date: '2025-07-30',
          created_by: 'Coach',
          opposition: 'FC United',
          image_url: 'https://media.istockphoto.com/id/1620960870/photo/happy-teenage-female-soccer-team-goal-celebration-run-and-congratulations-cheer.jpg?s=612x612&w=0&k=20&c=rPZ-6uJzgoxaiFTFloRoyUGoOP31On-i98jXdARwahk='
        }
      ];

      return (
        <div className="matches-container dashboard">
          <div className="matches-container-header container">
            <h2 className="section-title">
              Match Reports
              <span className="example-label">Example</span>
            </h2>
          </div>
          <div className="matches-scroll-container container">
            <div className="matches-scroll-wrapper">
              {dummyReports.map((report, index) => (
                <div className="report-card-wrapper" key={report.report_id}>
                  <div 
                    className="report-card"
                    onClick={() => navigate(`/match/${report.match_id}/report`)}
                  >
                    <div className="report-image-container">
                      <img 
                        src={report.image_url} 
                        alt={report.title} 
                        className="report-image"
                      />
                      {report.opposition && (
                        <div className="report-opposition">
                          vs {report.opposition}
                        </div>
                      )}
                    </div>
                    <div className="report-content">
                      <h3 className="report-title">{report.title}</h3>
                      <p className="report-excerpt">{report.excerpt}</p>
                      <div className="report-meta">
                        <div className="report-date">
                          <FaCalendarAlt />
                          <span>{formatDate(report.match_date)}</span>
                        </div>
                        <div className="report-author">
                          <FaUser />
                          <span>{report.created_by}</span>
                        </div>
                      </div>
                      <button className="read-more-button">
                        Read match report <FaArrowRight />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    } else {
      // No reports and not showing dummy data - show empty state
      return (
        <div className="matches-container dashboard">
          <div className="matches-container-header container">
            <h2 className="section-title">Match Reports</h2>
          </div>
          <div className="container">
            <p style={{color: '#585858', marginTop: '0'}}>No match reports available</p>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="matches-container dashboard">
      <div className="matches-container-header container">
        <h2 className="section-title">Match Reports</h2>
        {requiresPro && reports.length > 0 && (
          <div className="pro-feature-notice" onClick={() => setShowProModal(true)}>
            <FaCrown className="crown-icon" />
            <span>Upgrade to Pro to View All Match Reports</span>
          </div>
        )}
      </div>
      <div className="matches-scroll-container container">
        <div className="matches-scroll-wrapper">
          {reports.map((report, index) => (
            <div className="report-card-wrapper" key={report.report_id}>
              <div 
                className={`report-card ${requiresPro && index > 0 ? 'pro-locked' : ''}`}
                onClick={() => handleReportClick(report.match_id, index)}
              >
                <div className="report-image-container">
                  {requiresPro && index > 0 ? (
                    <div className="report-image-crown-overlay">
                      <FaCrown className="crown-icon" />
                      <span>Upgrade to Pro</span>
                    </div>
                  ) : report.image_url ? (
                    <img 
                      src={report.image_url} 
                      alt={report.title} 
                      className="report-image"
                    />
                  ) : (
                    <div className="report-image-placeholder">
                      <FaCalendarAlt />
                    </div>
                  )}
                  {report.opposition && (
                    <div className="report-opposition">
                      vs {report.opposition}
                    </div>
                  )}
                </div>
                <div className="report-content">
                  <h3 className="report-title">{report.title}</h3>
                  <p className="report-excerpt">{report.excerpt}</p>
                  <div className="report-meta">
                    <div className="report-date">
                      <FaCalendarAlt />
                      <span>{formatDate(report.match_date)}</span>
                    </div>
                    <div className="report-author">
                      <FaUser />
                      <span>{report.created_by}</span>
                    </div>
                  </div>
                  <button className="read-more-button">
                    Read match report <FaArrowRight />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <ProFeatureModal
        isOpen={showProModal}
        onClose={() => setShowProModal(false)}
        featureName="Match Reports"
      />
    </div>
  );
};

export default MatchReports; 